import { Modal, Table } from "antd";
import { type HTMLAttributes, useState } from "react";
import { useIntl } from "react-intl";
import CustomSelect from "~components/CustomSelect";
import { formItemSharedStyles } from "~constants/sharedStyles.ts";
import { RoleStatuses } from "~enums/roleStatuses.ts";
import { useCreateRole } from "~hooks/apiHooks/useCreateRole";
import { useDeleteRole } from "~hooks/apiHooks/useDeleteRole";
import { useUpdateRole } from "~hooks/apiHooks/useUpdateRole";
import { useHandlePagination } from "~hooks/useHandlePagination";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import { onTableChange } from "~utilities/onTableChange";
import { transformRoleResponse } from "~utilities/transformRoleResponse";
import classNames from "~utils/classNames";
import { sortStringValues } from "~utils/utilities";
import parentStyles from "../../AssignFixtureOfficialsPage.module.css";
import styles from "./AssignFixtureOfficialsModal.module.css";
import { AssignFixtureOfficialsForm } from "./components/AssignFixtureOfficialsForm";
import { useAssignFixtureOfficialsColumns } from "./hooks/useAssignFixtureOfficialsModalColumns";
import { useFetchFixtureRoles } from "./hooks/useFetchFixtureRoles";
import type { AssignFixtureOfficialsModalTableRow } from "./types/assignFixtureOfficialsTableRow";

type Props = {
  fixtureId: string | null;
  closeModal: () => void;
};

type TableCellProps = HTMLAttributes<HTMLTableCellElement> & {
  isEditing: boolean;
  record: AssignFixtureOfficialsModalTableRow;
};

const TableCell = ({ isEditing, record, ...props }: TableCellProps) => (
  <td {...props} className={classNames(props.className, isEditing ? styles.editCell : styles.cell)} />
);

const DEFAULT_SORT_FIELD = "role";

export const AssignFixtureOfficialsModal = ({ fixtureId, closeModal }: Props) => {
  const { formatMessage } = useIntl();

  const { fixtureOfficialRoles } = useSportConfiguration();

  const officialRoles = fixtureOfficialRoles.sort(sortStringValues);

  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination();
  const [sortBy, setSortBy] = useState<string>(DEFAULT_SORT_FIELD);
  const [statusFilter, setStatusFilter] = useState<RoleStatuses | undefined>();

  const statusFilterOptions = Object.values(RoleStatuses).map((status) => ({
    label: formatMessage({ id: `roleStatus.${status}` }),
    value: status,
  }));

  const params: Record<string, string> = {
    role: officialRoles.join(","),
    ...(statusFilter && { status: statusFilter }),
    limit: pageSize.toString(),
    offset: offset.toString(),
    sortBy,
    include: ["persons"].join(","),
  };

  const { data, isFetching, refetch } = useFetchFixtureRoles(fixtureId ?? "", params, transformRoleResponse, ["assignFixtureOfficials"], {
    enabled: !!fixtureId,
  });

  const selectProps = {
    allowClear: true,
    className: styles.select,
    disabled: isFetching,
  };

  const { mutateAsync: createRole, isPending: isCreating } = useCreateRole({
    onSuccess: () => refetch(),
  });
  const { mutateAsync: updateRole, isPending: isUpdating } = useUpdateRole({
    onSuccess: () => refetch(),
  });
  const { mutateAsync: deleteRole, isPending: isDeleting } = useDeleteRole({
    onSuccess: () => refetch(),
  });

  const isLoading = isFetching || isCreating || isUpdating || isDeleting;
  const [modal, contextHolder] = Modal.useModal();

  const columns = useAssignFixtureOfficialsColumns(
    isLoading,
    (payload) => updateRole(payload),
    (roleId) => deleteRole(roleId),
    modal,
  );

  const onChange = onTableChange<AssignFixtureOfficialsModalTableRow>(setSortBy, "nameFullLocal");

  return (
    <Modal
      open={!!fixtureId}
      onCancel={closeModal}
      title={formatMessage({ id: "officials.assign" })}
      cancelText={formatMessage({ id: "close" })}
      width={800}
      okButtonProps={{ hidden: true }}
    >
      {contextHolder}
      <div className={parentStyles.topBar}>
        <div className={parentStyles.topRightContainer}>
          <AssignFixtureOfficialsForm createRole={createRole} fixtureId={fixtureId} isParentLoading={isLoading} />
        </div>
      </div>
      <div style={formItemSharedStyles}>
        <CustomSelect
          options={statusFilterOptions}
          onChange={(status) => setStatusFilter(status as RoleStatuses)}
          placeholder={formatMessage({ id: "role.status" })}
          {...selectProps}
        />
      </div>
      <Table
        size="small"
        rowKey="roleId"
        dataSource={data}
        columns={columns}
        loading={isLoading}
        onChange={onChange}
        components={{ body: { cell: TableCell } }}
        rootClassName={styles.tableContainer}
        pagination={{
          current: currentPage,
          defaultPageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: pageSizeOptions,
          showQuickJumper: false,
          total: offset + (data?.length ?? 0) + 1,
          position: ["bottomCenter"],
          onChange: handlePaginationChange,
          showTotal: () => formatMessage({ id: "pageN" }, { number: currentPage }),
        }}
      />
    </Modal>
  );
};
